import { useTranslation } from 'react-i18next';
import {
  AddCouponByCodeDto,
  AddCouponByCodeResponse,
  AddItemByAccountDto,
  AddItemByCouponDto,
  CartStep,
  CartType,
  ChangeCurrencyForCartDto,
  CommonErrorsTranslations,
  ProductType,
  RemoveDiscountForCartDto,
  SubmitCartDto,
  UpdateDiscountForCartDto,
  UpdateTradingSystemDto,
  UseCreditsOnCartDto,
} from '@monorepo/types';
import { useErrorSnackbar } from '@hub/features/purchase/hooks';
import { QueryConfigKeys } from '@hub/utils/react-query/query-config';
import {
  addCouponByCode,
  addItemByAccount,
  addItemByCoupon,
  changeCurrency,
  creditsOnCart,
  removeDiscount,
  submitCart,
  updateCartStep,
  updateDiscount,
  updateTradingSystem,
} from '@hub/utils/react-query/query-functions';
import {
  addToCart,
  emptyCart,
  removeFromCart,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.CART;

// Mutation keys for tracking cart mutations globally
export const CART_MUTATION_KEYS = {
  UPDATE_DISCOUNT: ['cart', 'updateDiscount'],
  CREDITS_ON_CART: ['cart', 'creditsOnCart'],
  UPDATE_TRADING_SYSTEM: ['cart', 'updateTradingSystem'],
  REMOVE_FROM_CART: ['cart', 'removeFromCart'],
  REMOVE_DISCOUNT: ['cart', 'removeDiscount'],
  CHANGE_CURRENCY: ['cart', 'changeCurrency'],
  ADD_COUPON_BY_CODE: ['cart', 'addCouponByCode'],
};

export const useUpdateCartStep = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, CartStep>({
    mutationFn: updateCartStep,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to update cart step. Please try again.');
    },
  });
};

export const useUpdateDiscount = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, UpdateDiscountForCartDto>({
    mutationKey: CART_MUTATION_KEYS.UPDATE_DISCOUNT,
    mutationFn: updateDiscount,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to apply discount. Please try again.');
    },
  });
};

export const useUpdateTradingSystem = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, UpdateTradingSystemDto>({
    mutationKey: CART_MUTATION_KEYS.UPDATE_TRADING_SYSTEM,
    mutationFn: updateTradingSystem,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to change trading system. Please try again.');
    },
  });
};

export const useCreditsOnCart = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, UseCreditsOnCartDto>({
    mutationKey: CART_MUTATION_KEYS.CREDITS_ON_CART,
    mutationFn: creditsOnCart,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to apply credits to cart. Please try again.');
    },
  });
};

export const useRemoveDiscount = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, RemoveDiscountForCartDto>({
    mutationKey: CART_MUTATION_KEYS.REMOVE_DISCOUNT,
    mutationFn: removeDiscount,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to remove discount. Please try again.');
    },
  });
};

export const useChangeCurrency = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, ChangeCurrencyForCartDto>({
    mutationKey: CART_MUTATION_KEYS.CHANGE_CURRENCY,
    mutationFn: changeCurrency,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to change currency. Please try again.');
    },
  });
};

export const useAddToCart = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation({
    mutationFn: addToCart,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to add item to cart. Please try again.');
    },
  });
};

export const useRemoveFromCart = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation({
    mutationKey: CART_MUTATION_KEYS.REMOVE_FROM_CART,
    mutationFn: removeFromCart,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to remove item from cart. Please try again.');
    },
  });
};

export const useEmptyCart = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error>({
    mutationFn: emptyCart,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to empty cart. Please try again.');
    },
  });
};

export const useSubmitCart = () => {
  const { showError } = useErrorSnackbar();

  return useMutation<{ url: string }, Error, SubmitCartDto>({
    mutationFn: submitCart,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      showError(error);
    },
  });
};

export const useAddItemByCoupon = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();
  const { t } = useTranslation(['errors']);

  return useMutation<CartType, Error, AddItemByCouponDto>({
    mutationFn: addItemByCoupon,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
      // Invalidate coupons query so redeemed coupons disappear immediately
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[QueryConfigKeys.USERS].COUPONS,
      });
      // Invalidate coupon metrics to update counts after redemption
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[QueryConfigKeys.USERS].COUPON_METRICS,
      });
    },
    onError: (error) => {
      showError(error, error?.message || t('coupon_add_failed'));
    },
  });
};

export const useAddItemByAccount = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();

  return useMutation<CartType, Error, AddItemByAccountDto>({
    mutationFn: addItemByAccount,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS[CONFIG_KEY].DETAILS, data);
    },
    onError: (error) => {
      showError(error, 'Failed to add item to cart. Please try again.');
    },
  });
};

export const useAddCouponByCode = () => {
  const queryClient = useQueryClient();
  const { showError } = useErrorSnackbar();
  const { t } = useTranslation(['errors']);

  return useMutation<AddCouponByCodeResponse, Error, AddCouponByCodeDto>({
    mutationKey: CART_MUTATION_KEYS.ADD_COUPON_BY_CODE,
    mutationFn: addCouponByCode,
    onSuccess: (result) => {
      // Handle affiliation discount application
      if (result?.affiliationDiscountApplied) {
        // Invalidate cart to refresh cart data with affiliation discount
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS[CONFIG_KEY].DETAILS,
        });
        // Invalidate cart discounts to refresh discount options
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS[CONFIG_KEY].DISCOUNTS,
        });
        return;
      }

      const isGeneral =
        result?.coupon?.items?.[0]?.productType === ProductType.General;
      // Only invalidate cart if coupon is General
      if (isGeneral) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS[CONFIG_KEY].DETAILS,
        });
        // Invalidate cart discounts to refresh discount options
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS[CONFIG_KEY].DISCOUNTS,
        });
      }
      // Invalidate coupons query to refresh available coupons
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[QueryConfigKeys.USERS].COUPONS,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[QueryConfigKeys.USERS].COUPON_METRICS,
      });
    },
    onError: (error: Error & { data?: { message?: string } }) => {
      // Translate error message if it's a CommonErrors enum value
      const errorMessage = error?.data?.message;

      if (errorMessage && CommonErrorsTranslations[errorMessage]) {
        // Use translation for CommonErrors enum values
        const translationKey = CommonErrorsTranslations[errorMessage];
        showError(error, t(translationKey));
      } else if (errorMessage) {
        // Use the error message directly if it's not a CommonErrors enum value
        showError(error, errorMessage);
      } else {
        // Fallback to generic error message
        showError(error, t('something_wrong'));
      }
    },
  });
};
